import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'

import { BASE_URL } from '../const'

interface QueueItem {
  resolve: (value: string | null) => void
  reject: (error: unknown) => void
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const instance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Флаг для предотвращения множественных запросов на обновление токена
let isRefreshing = false
let failedQueue: QueueItem[] = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}

instance.interceptors.request.use((config) => {
  try {
    const authState = localStorage.getItem('auth-store')

    if (authState) {
      const { state } = JSON.parse(authState)

      if (state?.accessToken) {
        config.headers.Authorization = `Bearer ${state.accessToken}`
      }
    }
  } catch {
    // Corrupted auth state — continue without auth header
  }

  return config
})

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Проверяем код в данных ответа
    if (response.data?.code === 401) {
      return handleUnauthorized(response) as Promise<AxiosResponse>
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig

    // Проверяем статус код ответа
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      return handleUnauthorized(error.response, originalRequest, error)
    }

    return Promise.reject(error)
  }
)

const handleUnauthorized = async (
  response: AxiosResponse,
  originalRequest?: ExtendedAxiosRequestConfig,
  error?: AxiosError
): Promise<AxiosResponse> => {
  const authState = localStorage.getItem('auth-store')

  if (!authState) {
    redirectToLogin()
    return Promise.reject(error || response)
  }

  let state: Record<string, string>
  try {
    state = JSON.parse(authState).state
  } catch {
    redirectToLogin()
    return Promise.reject(error || response)
  }

  if (!state?.refreshToken) {
    redirectToLogin()
    return Promise.reject(error || response)
  }

  if (isRefreshing) {
    // Если уже идет процесс обновления токена, добавляем запрос в очередь
    return new Promise<AxiosResponse>((resolve, reject) => {
      failedQueue.push({
        resolve: (token) => {
          if (originalRequest && token) {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(instance(originalRequest))
          } else {
            reject(new Error('No token received'))
          }
        },
        reject,
      })
    })
  }

  if (originalRequest) {
    originalRequest._retry = true
  }
  isRefreshing = true

  try {
    // Делаем запрос на обновление токена
    const refreshResponse = await axios.post(`${BASE_URL}/auth/refresh-token`, {
      refreshToken: state.refreshToken,
    })

    const { accessToken, refreshToken } = refreshResponse.data

    // Обновляем токены в localStorage
    const updatedState = {
      ...state,
      accessToken,
      refreshToken,
    }
    localStorage.setItem('auth-store', JSON.stringify({ state: updatedState }))

    // Обрабатываем очередь запросов
    processQueue(null, accessToken)

    // Повторяем оригинальный запрос с новым токеном
    if (originalRequest) {
      originalRequest.headers.Authorization = `Bearer ${accessToken}`
      return instance(originalRequest)
    }

    // Если нет оригинального запроса, возвращаем пустой ответ
    return Promise.resolve({
      data: {},
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {},
    } as AxiosResponse)
  } catch (refreshError) {
    // Если обновление токена не удалось, очищаем состояние и перенаправляем на логин
    processQueue(refreshError, null)
    localStorage.removeItem('auth-store')
    redirectToLogin()
    return Promise.reject(refreshError)
  } finally {
    isRefreshing = false
  }
}

const redirectToLogin = () => {
  window.location.href = '/login'
}

export default instance
